<script setup lang="ts">
import SaveButton from "@/components/SaveButton.vue";
import ContactFormFields from "@/contacts/ContactFormFields.vue";
import type { Contact, ContactAddress } from "@/contacts/types";
import { Erase } from "@iconoir/vue";
import type { MutationStatus } from "@tanstack/vue-query";
import { useResizeObserver } from "@vueuse/core";
import SignaturePad from "signature_pad";
import { onMounted, ref, useTemplateRef, type Ref } from "vue";
import ConsentText from "./ConsentText.vue";
import type { ContactCreate } from "./types";

const contact = defineModel<ContactCreate>({ required: true });

defineProps<{
  address: ContactAddress;
  savingStatus: MutationStatus;
}>();

const emit = defineEmits<{
  save: [];
}>();

const signatureCanvas = useTemplateRef("signature-canvas");
const canvasSize = ref({
  width: 330,
  height: 100,
});

const pad: Ref<SignaturePad | null> = ref(null);
const signatureSvgError = ref(false);

useResizeObserver(signatureCanvas, (entries) => {
  const entry = entries[0];
  pad.value?.clear();
  canvasSize.value = {
    width: entry?.contentRect.width ?? 330,
    height: entry?.contentRect.height ?? 100,
  };
});

onMounted(() => {
  pad.value = new SignaturePad(signatureCanvas.value!);
});

const updateContact = (updatedContact: Contact) => {
  contact.value = {
    ...contact.value,
    ...updatedContact,
  };
};

const save = () => {
  if (pad.value?.isEmpty()) {
    signatureSvgError.value = true;
    return;
  }

  signatureSvgError.value = false;

  contact.value.consent = {
    signatureSvg: pad.value?.toSVG() ?? "",
  };

  emit("save");
};
</script>

<template>
  <form @submit.prevent="save">
    <div class="container container-lg box column">
      <ContactFormFields
        :model-value="contact"
        :address="address"
        @update:model-value="updateContact"
      />

      <fieldset class="column column-lg column-self-space-xl">
        <ConsentText ref="consent-text" />

        <label for="consent"
          ><input id="consent" type="checkbox" required />Ich bin mit der
          Verarbeitung einverstanden.</label
        >

        <div class="column">
          <label for="signature">Unterschrift</label>
          <canvas
            id="signature"
            ref="signature-canvas"
            :width="canvasSize.width"
            :height="canvasSize.height"
            :aria-invalid="signatureSvgError"
          ></canvas>
          <small v-if="signatureSvgError">Unterschrift benötigt</small>
          <button
            type="button"
            class="button button--secondary"
            @click="pad?.clear()"
          >
            <Erase /> Unterschrift zurücksetzen
          </button>
        </div>
      </fieldset>
      <div class="row row-end">
        <SaveButton :saving-status="savingStatus">Speichern</SaveButton>
      </div>
    </div>
  </form>
</template>

<style scoped>
canvas {
  inline-size: 100%;
  aspect-ratio: 3/1;
  background-color: var(--bg-color-card);
  border-radius: var(--border-radius-card);
  box-shadow: var(--shadow);
  touch-action: none;
}
</style>
